/**
 * MetaAdsSettings - Meta Ads Integration Settings (MA-META-UI + MA-META-P3)
 * 
 * Allows users to:
 * - Connect Meta account via OAuth or access token
 * - View connected accounts
 * - Enable/disable auto-sync
 * - Trigger manual sync
 * - Monitor sync status and job history
 * 
 * Supports two connection methods:
 * 1. OAuth flow for normal users
 * 2. Manual access token for agencies using System Users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Facebook,
  Link2,
  Unlink,
  RefreshCw,
  Check,
  X,
  AlertCircle,
  Loader2,
  Clock,
  Key,
  Building2,
  Wallet,
  TrendingUp,
  ExternalLink,
  Info,
  Zap,
  Calendar,
  Play,
  History,
  Settings2,
  CheckCircle2,
  XCircle,
  Timer,
  Shield,
  Eye,
  EyeOff,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import CampaignInclusionModal from './CampaignInclusionModal';
import CampaignMeasurementManager from './CampaignMeasurementManager';

// Status badge colors
const STATUS_COLORS = {
  connected: 'bg-green-500/20 text-green-400 border-green-500/30',
  syncing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  skipped: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

/**
 * Connection Status Card
 */
function ConnectionStatusCard({ connected, businessId, connectedAt, accountCount, onDisconnect }) {
  return (
    <Card className="glass border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
            <Facebook className="h-5 w-5 text-blue-400" />
            Meta Connection
          </CardTitle>
          <Badge className={cn("text-xs", connected ? STATUS_COLORS.connected : STATUS_COLORS.inactive)}>
            {connected ? 'Connected' : 'Not Connected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {connected ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Business ID</p>
                <p className="text-slate-100 font-mono">{businessId}</p>
              </div>
              <div>
                <p className="text-slate-400">Connected</p>
                <p className="text-slate-100">
                  {connectedAt ? formatDistanceToNow(new Date(connectedAt), { addSuffix: true }) : 'Unknown'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="text-sm text-slate-400">
                {accountCount} ad account{accountCount !== 1 ? 's' : ''} synced
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={onDisconnect}
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Connect your Meta Business account to sync ad campaigns, spend data, and performance metrics.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Auto-Sync Settings Card (MA-META-P3)
 */
function AutoSyncSettingsCard({ schedulerStatus, onUpdateSettings, onTriggerSync, loading }) {
  const [updating, setUpdating] = useState(false);
  const [triggering, setTriggering] = useState(null);
  
  const handleToggle = async (field, value) => {
    setUpdating(true);
    try {
      await onUpdateSettings({ [field]: value });
      toast({
        title: 'Settings updated',
        description: `Auto-sync ${field.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      toast({
        title: 'Failed to update',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };
  
  const handleTriggerSync = async (syncType) => {
    setTriggering(syncType);
    try {
      await onTriggerSync(syncType);
      toast({
        title: 'Sync started',
        description: `${syncType.charAt(0).toUpperCase() + syncType.slice(1)} sync initiated.`,
      });
    } catch (error) {
      toast({
        title: 'Sync failed',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setTriggering(null);
    }
  };
  
  if (loading || !schedulerStatus) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="glass border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
            <Settings2 className="h-5 w-5 text-indigo-400" />
            Auto-Sync Settings
          </CardTitle>
          {schedulerStatus.is_syncing && (
            <Badge className={cn("text-xs", STATUS_COLORS.running)}>
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Syncing
            </Badge>
          )}
        </div>
        <CardDescription className="text-slate-400 text-sm">
          Keep your advertising data fresh with automatic background sync.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Master Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              schedulerStatus.auto_sync_enabled ? "bg-green-500/20" : "bg-slate-500/20"
            )}>
              <Zap className={cn(
                "h-4 w-4",
                schedulerStatus.auto_sync_enabled ? "text-green-400" : "text-slate-400"
              )} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-100">Enable Auto-Sync</p>
              <p className="text-xs text-slate-400">Automatically sync data on schedule</p>
            </div>
          </div>
          <Switch
            checked={schedulerStatus.auto_sync_enabled}
            onCheckedChange={(checked) => handleToggle('auto_sync_enabled', checked)}
            disabled={updating}
            data-testid="auto-sync-toggle"
          />
        </div>
        
        {/* Sync Type Controls */}
        {schedulerStatus.auto_sync_enabled && (
          <div className="space-y-3 pt-2">
            {/* Structure Sync */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-blue-400" />
                <div>
                  <p className="text-sm text-slate-200">Structure Sync</p>
                  <p className="text-xs text-slate-400">Accounts, campaigns, ad sets, ads, creatives • Daily</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={schedulerStatus.structure_sync_enabled !== false}
                  onCheckedChange={(checked) => handleToggle('structure_sync_enabled', checked)}
                  disabled={updating}
                  data-testid="structure-sync-toggle"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTriggerSync('structure')}
                  disabled={triggering || schedulerStatus.is_syncing}
                  className="h-8"
                  data-testid="trigger-structure-sync"
                >
                  {triggering === 'structure' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
            
            {/* Insights Sync */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                <div>
                  <p className="text-sm text-slate-200">Insights Sync</p>
                  <p className="text-xs text-slate-400">Spend, impressions, clicks • Hourly</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={schedulerStatus.insights_sync_enabled !== false}
                  onCheckedChange={(checked) => handleToggle('insights_sync_enabled', checked)}
                  disabled={updating}
                  data-testid="insights-sync-toggle"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTriggerSync('insights')}
                  disabled={triggering || schedulerStatus.is_syncing}
                  className="h-8"
                  data-testid="trigger-insights-sync"
                >
                  {triggering === 'insights' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Last Sync Info */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
          <SyncStatusSummary
            label="Structure"
            lastSuccess={schedulerStatus.last_structure_success_at}
            lastError={schedulerStatus.last_structure_error}
            lastJob={schedulerStatus.last_structure_sync}
          />
          <SyncStatusSummary
            label="Insights"
            lastSuccess={schedulerStatus.last_insights_success_at}
            lastError={schedulerStatus.last_insights_error}
            lastJob={schedulerStatus.last_insights_sync}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Sync Status Summary Component
 */
function SyncStatusSummary({ label, lastSuccess, lastError, lastJob }) {
  const hasError = lastError && (!lastSuccess || new Date(lastError) > new Date(lastSuccess));
  
  return (
    <div className="p-2 rounded bg-white/[0.02]">
      <div className="flex items-center gap-2 mb-1">
        {hasError ? (
          <XCircle className="h-3 w-3 text-red-400" />
        ) : lastSuccess ? (
          <CheckCircle2 className="h-3 w-3 text-green-400" />
        ) : (
          <Timer className="h-3 w-3 text-slate-400" />
        )}
        <span className="text-xs font-medium text-slate-300">{label}</span>
      </div>
      {lastSuccess ? (
        <p className="text-xs text-slate-400">
          Last success: {formatDistanceToNow(new Date(lastSuccess), { addSuffix: true })}
        </p>
      ) : hasError ? (
        <p className="text-xs text-red-400 truncate" title={lastError}>
          Error: {lastError?.substring(0, 30)}...
        </p>
      ) : (
        <p className="text-xs text-slate-500">Never synced</p>
      )}
    </div>
  );
}

/**
 * Connected Account Row
 */
function AccountRow({ account, onSync }) {
  const [syncing, setSyncing] = useState(false);
  
  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync(account.id);
    } finally {
      setSyncing(false);
    }
  };
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-500/20">
          <Wallet className="h-4 w-4 text-blue-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-100">{account.name}</p>
          <p className="text-xs text-slate-400">
            {account.currency} • {account.external_id}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={cn("text-xs", STATUS_COLORS[account.status] || STATUS_COLORS.inactive)}>
          {account.status}
        </Badge>
        {account.last_synced_at && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="text-xs text-slate-400">
                  {formatDistanceToNow(new Date(account.last_synced_at), { addSuffix: true })}
                </span>
              </TooltipTrigger>
              <TooltipContent>Last synced</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="h-8 w-8 p-0"
        >
          <RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} />
        </Button>
      </div>
    </div>
  );
}

/**
 * Sync Job History Panel (MA-META-P3)
 */
function SyncJobHistoryPanel({ jobs, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (!jobs || jobs.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No sync history yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {jobs.map((job) => (
        <div
          key={job.id}
          className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/[0.05]"
        >
          <div className="flex items-center gap-2">
            <Badge className={cn("text-xs", STATUS_COLORS[job.status] || STATUS_COLORS.pending)}>
              {job.status}
            </Badge>
            <span className="text-sm text-slate-300 capitalize">
              {job.sync_type.replace('_', ' ')}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {job.records_synced > 0 && (
              <span className="text-green-400">{job.records_synced} synced</span>
            )}
            {job.records_failed > 0 && (
              <span className="text-red-400">{job.records_failed} failed</span>
            )}
            {job.started_at && (
              <span>{formatDistanceToNow(new Date(job.started_at), { addSuffix: true })}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Sync Status Panel (Legacy)
 */
function SyncStatusPanel({ syncStatus, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (!syncStatus || syncStatus.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No sync history yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {syncStatus.map((status, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/[0.05]"
        >
          <div className="flex items-center gap-2">
            <Badge className={cn("text-xs", STATUS_COLORS[status.sync_status] || STATUS_COLORS.pending)}>
              {status.sync_status}
            </Badge>
            <span className="text-sm text-slate-300 capitalize">
              {status.resource_type.replace('_', ' ')}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span>{status.synced_records}/{status.total_records} records</span>
            {status.last_synced_at && (
              <span>{formatDistanceToNow(new Date(status.last_synced_at), { addSuffix: true })}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * OAuth Config Form (Admin Only)
 */
function OAuthConfigForm({ form, setForm, saving, onSave, onCancel, suggestedRedirectUri }) {
  const [showSecret, setShowSecret] = useState(false);
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-slate-200 text-sm">App ID</Label>
        <Input
          value={form.app_id}
          onChange={(e) => setForm(f => ({ ...f, app_id: e.target.value }))}
          placeholder="123456789012345"
          className="bg-white/5 border-white/10 font-mono text-sm"
          data-testid="oauth-config-app-id-input"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-200 text-sm">App Secret</Label>
        <div className="relative">
          <Input
            type={showSecret ? "text" : "password"}
            value={form.app_secret}
            onChange={(e) => setForm(f => ({ ...f, app_secret: e.target.value }))}
            placeholder="Enter app secret"
            className="bg-white/5 border-white/10 font-mono text-sm pr-10"
            autoComplete="off"
            data-testid="oauth-config-app-secret-input"
          />
          <button
            type="button"
            onClick={() => setShowSecret(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
            tabIndex={-1}
          >
            {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-slate-500">Never shown again after save.</p>
      </div>
      
      <div className="flex items-center gap-2 pt-2">
        <Button
          onClick={onSave}
          disabled={saving || !form.app_id || !form.app_secret}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="oauth-config-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} className="text-slate-400">
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Main Component
 */
export function MetaAdsSettings() {
  // State
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [syncStatus, setSyncStatus] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  
  // OAuth config state (admin-only)
  const [oauthConfig, setOauthConfig] = useState(null);
  const [oauthConfigLoading, setOauthConfigLoading] = useState(false);
  const [oauthConfigForm, setOauthConfigForm] = useState({ app_id: '', app_secret: '' });
  const [oauthConfigSaving, setOauthConfigSaving] = useState(false);
  const [showOAuthConfig, setShowOAuthConfig] = useState(false);
  
  // Campaign inclusion state (MA-CAMPAIGN-INCLUSION-LAYER-035)
  const [showInclusionModal, setShowInclusionModal] = useState(false);
  
  // Check if user is admin (user data stored under 'scriptcompass_user' key)
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('scriptcompass_user') || '{}'); } catch { return {}; } })();
  const userIsAdmin = storedUser?.role === 'admin' || storedUser?.role === 'super_admin';
  
  // Form state
  const [accessToken, setAccessToken] = useState('');
  const [businessId, setBusinessId] = useState('');
  
  // Load OAuth config (admin only)
  const loadOAuthConfig = useCallback(async () => {
    if (!userIsAdmin) return;
    setOauthConfigLoading(true);
    try {
      const res = await api.get('/meta-sync/oauth/config');
      setOauthConfig(res.data);
      if (res.data?.configured) {
        setOauthConfigForm(f => ({ ...f, app_id: res.data.app_id || '' }));
      }
    } catch {
      // Not admin or endpoint not available
    } finally {
      setOauthConfigLoading(false);
    }
  }, [userIsAdmin]);
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, accountsRes, statusRes, schedulerRes, jobsRes] = await Promise.all([
        api.get('/meta-sync/connection-status'),
        api.get('/meta-sync/accounts'),
        api.get('/meta-sync/status'),
        api.get('/meta-sync/scheduler/status').catch(() => ({ data: null })),
        api.get('/meta-sync/scheduler/jobs?limit=10').catch(() => ({ data: [] })),
      ]);
      
      setConnectionStatus(connRes.data);
      setAccounts(accountsRes.data || []);
      setSyncStatus(statusRes.data || []);
      setSchedulerStatus(schedulerRes.data);
      setJobHistory(jobsRes.data || []);
    } catch (error) {
      console.error('Failed to load Meta sync data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
    loadOAuthConfig();
    // Check for unreviewed campaigns on initial load
    checkForUnreviewedCampaigns();
  }, [loadData, loadOAuthConfig]);
  
  // Save OAuth config
  const handleSaveOAuthConfig = async () => {
    if (!oauthConfigForm.app_id || !oauthConfigForm.app_secret) {
      toast({ title: 'Missing fields', description: 'App ID and App Secret are required.', variant: 'destructive' });
      return;
    }
    setOauthConfigSaving(true);
    try {
      await api.post('/meta-sync/oauth/config', oauthConfigForm);
      toast({ title: 'Configuration saved', description: 'Meta OAuth credentials saved securely.' });
      setOauthConfigForm(f => ({ ...f, app_secret: '' }));
      setShowOAuthConfig(false);
      loadOAuthConfig();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Could not save config.', variant: 'destructive' });
    } finally {
      setOauthConfigSaving(false);
    }
  };
  
  // Connect with access token
  const handleTokenConnect = async () => {
    if (!accessToken || !businessId) {
      toast({
        title: 'Missing fields',
        description: 'Please enter both access token and business ID.',
        variant: 'destructive',
      });
      return;
    }
    
    setConnecting(true);
    try {
      const response = await api.post('/meta-sync/connect', {
        access_token: accessToken,
        business_id: businessId,
      });
      
      toast({
        title: 'Connected successfully',
        description: response.data.message || `Synced ${response.data.accounts_synced} account(s)`,
      });
      
      setShowTokenDialog(false);
      setAccessToken('');
      setBusinessId('');
      loadData();
    } catch (error) {
      toast({
        title: 'Connection failed',
        description: error.response?.data?.detail || 'Failed to connect Meta account.',
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };
  
  // Disconnect
  const handleDisconnect = async () => {
    if (!accounts.length) return;
    
    try {
      // Disconnect first account (or all)
      for (const account of accounts) {
        await api.post('/meta-sync/disconnect', {
          account_id: account.id,
        });
      }
      
      toast({
        title: 'Disconnected',
        description: 'Meta account has been disconnected.',
      });
      
      loadData();
    } catch (error) {
      toast({
        title: 'Disconnect failed',
        description: error.response?.data?.detail || 'Failed to disconnect.',
        variant: 'destructive',
      });
    }
  };
  
  // Check for unreviewed campaigns after sync
  const checkForUnreviewedCampaigns = async () => {
    try {
      const res = await api.get('/campaign-inclusion/campaigns?filter=unreviewed');
      if (res.data?.unreviewed_count > 0) {
        setShowInclusionModal(true);
      }
    } catch {
      // Non-critical
    }
  };
  
  // Trigger sync (legacy)
  const handleSync = async (accountId, syncType = 'full') => {
    try {
      await api.post('/meta-sync/trigger', {
        account_id: accountId,
        sync_type: syncType,
      });
      
      toast({
        title: 'Sync started',
        description: `${syncType} sync initiated for account.`,
      });
      
      // Refresh status and check for new campaigns
      setTimeout(() => {
        loadData();
        checkForUnreviewedCampaigns();
      }, 2000);
    } catch (error) {
      toast({
        title: 'Sync failed',
        description: error.response?.data?.detail || 'Failed to start sync.',
        variant: 'destructive',
      });
    }
  };
  
  // Update scheduler settings
  const handleUpdateSchedulerSettings = async (settings) => {
    await api.put('/meta-sync/scheduler/settings', settings);
    // Refresh status after update
    const res = await api.get('/meta-sync/scheduler/status');
    setSchedulerStatus(res.data);
  };
  
  // Trigger scheduler sync
  const handleTriggerSchedulerSync = async (syncType) => {
    await api.post('/meta-sync/scheduler/trigger', { sync_type: syncType });
    // Refresh data and check for new campaigns
    setTimeout(() => {
      loadData();
      checkForUnreviewedCampaigns();
    }, 2000);
  };
  
  // MA-META-OAUTH-034: Real OAuth connect flow
  const handleOAuthConnect = async () => {
    try {
      const resp = await api.get('/meta-sync/oauth/start');
      if (resp.data?.auth_url) {
        window.location.href = resp.data.auth_url;
      } else {
        toast({
          title: 'OAuth not available',
          description: resp.data?.detail || 'Could not generate Meta authorization URL.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'OAuth Error',
        description: err?.response?.data?.detail || 'Failed to start Meta OAuth. Check server configuration.',
        variant: 'destructive',
      });
    }
  };
  
  const isConnected = connectionStatus?.connected;
  
  return (
    <div className="space-y-6" data-testid="meta-ads-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
            <Facebook className="h-6 w-6 text-blue-400" />
            Meta Ads Integration
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Connect your Meta Business account to sync campaigns and performance data.
          </p>
        </div>
        
        {!isConnected && (
          <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Link2 className="h-4 w-4 mr-2" />
                Connect Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg bg-slate-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-slate-100">Connect Meta Ads</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Choose how you want to connect your Meta Business account.
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="token" className="mt-4">
                <TabsList className="grid w-full grid-cols-2 bg-white/5">
                  <TabsTrigger value="oauth" className="data-[state=active]:bg-white/10">
                    <Zap className="h-4 w-4 mr-2" />
                    OAuth
                  </TabsTrigger>
                  <TabsTrigger value="token" className="data-[state=active]:bg-white/10">
                    <Key className="h-4 w-4 mr-2" />
                    Access Token
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="oauth" className="mt-4 space-y-4">
                  <Alert className="bg-blue-500/10 border-blue-500/20">
                    <Info className="h-4 w-4 text-blue-400" />
                    <AlertTitle className="text-blue-400">Quick Connect</AlertTitle>
                    <AlertDescription className="text-blue-300/80">
                      Sign in with your Meta account to automatically connect your ad accounts.
                    </AlertDescription>
                  </Alert>
                  
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleOAuthConnect}
                  >
                    <Facebook className="h-4 w-4 mr-2" />
                    Continue with Meta
                  </Button>
                </TabsContent>
                
                <TabsContent value="token" className="mt-4 space-y-4">
                  <Alert className="bg-amber-500/10 border-amber-500/20">
                    <Key className="h-4 w-4 text-amber-400" />
                    <AlertTitle className="text-amber-400">For Agencies & System Users</AlertTitle>
                    <AlertDescription className="text-amber-300/80">
                      Enter a long-lived access token from Meta Business Settings.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_id" className="text-slate-200">
                        Business ID
                      </Label>
                      <Input
                        id="business_id"
                        placeholder="123456789012345"
                        value={businessId}
                        onChange={(e) => setBusinessId(e.target.value)}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-xs text-slate-400">
                        Find in Meta Business Settings → Business Info
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="access_token" className="text-slate-200">
                        Access Token
                      </Label>
                      <Input
                        id="access_token"
                        type="password"
                        placeholder="EAAxxxxxxx..."
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                        className="bg-white/5 border-white/10 font-mono text-xs"
                      />
                      <p className="text-xs text-slate-400">
                        System User token with <code className="text-blue-400">ads_read</code> permission
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    className="w-full"
                    onClick={handleTokenConnect}
                    disabled={connecting || !accessToken || !businessId}
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Connect with Token
                      </>
                    )}
                  </Button>
                  
                  <a
                    href="https://business.facebook.com/settings/system-users"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-xs text-blue-400 hover:underline"
                  >
                    Get token from Meta Business Settings
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {/* OAuth Configuration (Admin Only) */}
      {userIsAdmin && (
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
                <Shield className="h-5 w-5 text-amber-400" />
                Meta OAuth Configuration
              </CardTitle>
              {oauthConfig?.configured ? (
                <Badge className={cn("text-xs", STATUS_COLORS.connected)} data-testid="oauth-config-status">
                  Configured
                </Badge>
              ) : (
                <Badge className={cn("text-xs", STATUS_COLORS.error)} data-testid="oauth-config-status">
                  Not Configured
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Admin-only. Credentials are stored in the database and never logged.
            </p>
          </CardHeader>
          <CardContent>
            {oauthConfigLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : oauthConfig?.configured && !showOAuthConfig ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">App ID</p>
                    <p className="text-slate-100 font-mono text-xs" data-testid="oauth-config-app-id">{oauthConfig.app_id}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">App Secret</p>
                    <p className="text-slate-100 font-mono text-xs" data-testid="oauth-config-secret-status">{"••••••••"}</p>
                  </div>
                </div>
                <div className="text-sm">
                  <p className="text-slate-400">Redirect URI <span className="text-slate-500">(auto-detected)</span></p>
                  <p className="text-slate-100 font-mono text-xs break-all" data-testid="oauth-config-redirect">{oauthConfig.redirect_uri}</p>
                  <p className="text-xs text-amber-400/70 mt-1">Add this URL to your Meta App → Facebook Login → Valid OAuth Redirect URIs. Add both your production and preview URLs there.</p>
                </div>
                <div className="flex justify-end pt-2 border-t border-white/10">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowOAuthConfig(true)}
                    className="text-amber-400 hover:text-amber-300"
                    data-testid="oauth-config-update-btn"
                  >
                    <Settings2 className="h-4 w-4 mr-1" />
                    Update Credentials
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {oauthConfig?.redirect_uri && (
                  <div className="text-sm p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                    <p className="text-slate-400 mb-1">Redirect URI <span className="text-slate-500">(auto-detected — add to your Meta App)</span></p>
                    <p className="text-slate-100 font-mono text-xs break-all" data-testid="oauth-config-redirect">{oauthConfig.redirect_uri}</p>
                  </div>
                )}
                <OAuthConfigForm
                  form={oauthConfigForm}
                  setForm={setOauthConfigForm}
                  saving={oauthConfigSaving}
                  onSave={handleSaveOAuthConfig}
                  onCancel={oauthConfig?.configured ? () => setShowOAuthConfig(false) : null}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Connection Status */}
      <ConnectionStatusCard
        connected={isConnected}
        businessId={connectionStatus?.business_id}
        connectedAt={connectionStatus?.connected_at}
        accountCount={connectionStatus?.account_count || 0}
        onDisconnect={handleDisconnect}
      />
      
      {/* Auto-Sync Settings (MA-META-P3) */}
      {isConnected && (
        <AutoSyncSettingsCard
          schedulerStatus={schedulerStatus}
          onUpdateSettings={handleUpdateSchedulerSettings}
          onTriggerSync={handleTriggerSchedulerSync}
          loading={loading}
        />
      )}
      
      {/* Connected Accounts */}
      {isConnected && accounts.length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
                <Building2 className="h-5 w-5 text-indigo-400" />
                Ad Accounts
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSync(accounts[0]?.id, 'full')}
                className="text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Sync All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {accounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  onSync={(id) => handleSync(id, 'full')}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Campaign Measurement Manager (MA-CAMPAIGN-INCLUSION-LAYER-035) */}
      {isConnected && (
        <CampaignMeasurementManager />
      )}
      
      {/* Sync Job History (MA-META-P3) */}
      {isConnected && jobHistory.length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
                <History className="h-5 w-5 text-amber-400" />
                Sync History
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadData} className="text-xs">
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <SyncJobHistoryPanel jobs={jobHistory} loading={loading} />
          </CardContent>
        </Card>
      )}
      
      {/* Legacy Sync Status */}
      {isConnected && syncStatus.length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-100">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Resource Sync Status
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <SyncStatusPanel syncStatus={syncStatus} loading={loading} />
          </CardContent>
        </Card>
      )}
      
      {/* Help Text */}
      <Alert className="bg-white/[0.02] border-white/10">
        <Info className="h-4 w-4 text-slate-400" />
        <AlertTitle className="text-slate-300">How auto-sync works</AlertTitle>
        <AlertDescription className="text-slate-400 text-sm">
          When enabled, campaign structure (accounts, campaigns, ad sets) syncs daily at 3:00 AM UTC. 
          Performance metrics (spend, impressions, clicks) sync hourly. You can also trigger a manual 
          sync anytime. Failed syncs are isolated per tenant and automatically retried.
        </AlertDescription>
      </Alert>
      
      {/* Campaign Inclusion Modal (MA-CAMPAIGN-INCLUSION-LAYER-035) */}
      <CampaignInclusionModal
        open={showInclusionModal}
        onOpenChange={setShowInclusionModal}
        onComplete={loadData}
      />
    </div>
  );
}

export default MetaAdsSettings;
